import { appStorage, appSession } from './lib/safeStorage';
import React, { useState, useEffect, lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import useBuildRefresh from "./hooks/useBuildRefresh";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { FeaturesProvider } from "./context/FeaturesContext";
import { AutoSaveProvider } from "./context/AutoSaveContext";
import { BudgetSyncProvider } from "./context/BudgetSyncContext";
import { EventProvider } from "./context/EventContext";
import { NotificationProvider } from "./context/NotificationProvider";
import { TooltipProvider } from "./components/ui/tooltip";
import ErrorBoundary from "./components/ErrorBoundary";
import PullToRefresh from "./components/PullToRefresh";
import BuildUpdateBar from "./components/BuildUpdateBar";
// ── Route-level code splitting ──────────────────────────────────────────
// Page components are lazy-loaded so each becomes its own JS chunk instead
// of bloating main.js. Heavy libs (recharts, qrcode) ride along into the
// chunks that use them. The app shell below (providers, DashboardLayout,
// RouteGuard, tours, toasters, ErrorBoundary) stays eager. JSX usage is
// unchanged — <Suspense> around each <Routes> block covers the load moment.
const CheckAvailability = lazy(() => import("./pages/CheckAvailability"));
const CallConfirmPage = lazy(() => import("./pages/CallConfirmPage"));
const CallReschedulePage = lazy(() => import("./pages/CallReschedulePage"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const EventList = lazy(() => import("./pages/EventList"));
const Locations = lazy(() => import("./pages/Locations"));
const ReminderHealthPage = lazy(() => import("./pages/ReminderHealthPage"));
const Clients = lazy(() => import("./pages/Clients"));
const StaffList = lazy(() => import("./pages/StaffList"));
const StaffRoles = lazy(() => import("./pages/StaffRoles"));
const StaffAvailability = lazy(() => import("./pages/StaffAvailability"));
const TeamAnnouncement = lazy(() => import("./pages/TeamAnnouncement"));
const Team = lazy(() => import("./pages/Team"));
const HeatLeaderboard = lazy(() => import("./pages/HeatLeaderboard"));
const EventBudgets = lazy(() => import("./pages/EventBudgets"));
const PayoutsPage = lazy(() => import("./pages/PayoutsPage"));
const ReviewsActivityPage = lazy(() => import("./pages/ReviewsActivityPage"));
const GPSCheckin = lazy(() => import("./pages/GPSCheckin"));
const Features = lazy(() => import("./pages/Features"));
const PreferredVendors = lazy(() => import("./pages/Settings/PreferredVendors"));
const VendorDirectory = lazy(() => import("./pages/VendorDirectory"));
const VenueDirectory = lazy(() => import("./pages/VenueDirectory"));
const StaffRegister = lazy(() => import("./pages/StaffRegister"));
const TeamRegister = lazy(() => import("./pages/TeamRegister"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const MyControlCenter = lazy(() => import("./pages/MyControlCenter"));
const MyChats = lazy(() => import("./pages/MyChats"));
const StaffMyProfile = lazy(() => import("./pages/StaffMyProfile"));
const StaffPortal = lazy(() => import("./pages/StaffPortal"));
const StaffHelpCenter = lazy(() => import("./pages/StaffHelpCenter"));
const StaffAnnouncementsPage = lazy(() => import("./pages/StaffAnnouncementsPage"));
const MyAssignmentsPage = lazy(() => import("./pages/MyAssignmentsPage"));
const MyAvailability = lazy(() => import("./pages/MyAvailability"));
const AdminVerifications = lazy(() => import("./pages/AdminVerifications"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const UnattributedLeadsPage = lazy(() => import("./pages/UnattributedLeadsPage"));
const EntitlementsAdminPage = lazy(() => import("./pages/EntitlementsAdminPage"));
const PaymentSweepRepairPage = lazy(() => import("./pages/PaymentSweepRepairPage"));
const PaymentReminderScansPage = lazy(() => import("./pages/PaymentReminderScansPage"));
const PaymentFeeSettingsPage = lazy(() => import("./pages/PaymentFeeSettingsPage"));
const SetupPage = lazy(() => import("./pages/settings/SetupPage"));
const EventTypesPage = lazy(() => import("./pages/settings/EventTypesPage"));
const EmbedCodePage = lazy(() => import("./pages/settings/EmbedCodePage"));
const PackageContentsEditor = lazy(() => import("./pages/settings/PackageContentsEditor"));
const EventWorkspacePage = lazy(() => import("./pages/EventWorkspacePage"));
const EventPrintPage = lazy(() => import("./pages/EventPrintPage"));
// NOT code-split, on purpose (Sep 2026): the client portal and the payment
// success page are the two highest-consequence pages in the product and the
// least tolerant of a code chunk that never arrives (Sentry
// JAVASCRIPT-REACT-D, 9 clients, path /client-view/<token>). Shipping them
// inside the main bundle makes this class of failure impossible there. No
// other route's code-splitting is changed. Measured cost: main.js goes from
// 263,677 to 328,432 bytes gzipped (+64,755 B = +63.2 KB gz).
import ClientViewPage from "./pages/ClientViewPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
const SongRequestPage = lazy(() => import("./pages/SongRequestPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const EventsAutomation = lazy(() => import("./pages/EventsAutomation"));
const EventPipelineBoardPage = lazy(() => import("./pages/EventPipelineBoardPage"));
const PipelinePage = lazy(() => import("./pages/PipelinePage"));
const EmbedLeadForm = lazy(() => import("./pages/EmbedLeadForm"));
const LiveExperiencePage = lazy(() => import("./pages/LiveExperiencePage"));
const DigitalCardPage = lazy(() => import("./pages/DigitalCardPage"));
const LiveLeadsPage = lazy(() => import("./pages/LiveLeadsPage"));
const QrCodesPage = lazy(() => import("./pages/QrCodesPage"));
const EventFeedbackPage = lazy(() => import("./pages/EventFeedbackPage"));
const LiveControlCenter = lazy(() => import("./pages/LiveControlCenter"));
const LiveAllEvents = lazy(() => import("./pages/LiveAllEvents"));
const BriefView = lazy(() => import("./pages/BriefView"));
const MotionDemo = lazy(() => import("./pages/internal/MotionDemo"));
import { isPlatformOwner } from "./lib/sidebarFilter";
import { hasFlag } from "./lib/entitlements";
import LegacyControlCenterRedirect from "./components/LegacyControlCenterRedirect";
import LegacyBriefRedirect from "./components/LegacyBriefRedirect";
import DashboardLayout from "./components/layout/DashboardLayout";
import AISupportAgent from "./components/AISupportAgent";
import PendingCallNotifier from "./components/PendingCallNotifier";
import ReviewCheckinNotifier from "./components/ReviewCheckinNotifier";
import PackageRequestsDock from "./components/PackageRequestsDock";
import QuickRatingModal from "./components/modals/QuickRatingModal";
import BetaFeedbackButton from "./components/BetaFeedbackButton";
import OnboardingTour from "./components/OnboardingTour";
import CommandPalette from "./components/CommandPalette";
import HowToCard from "./components/help/HowToCard";
import EnvironmentBadge from "./components/EnvironmentBadge";
import RouteGuard from "./components/RouteGuard";
import { RolePreviewProvider, useRolePreview } from "./context/RolePreviewContext";
import { RolePreviewBanner } from "./components/RolePreviewSwitcher";
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "./components/ui/sonner";
import { Check as ToastCheck, AlertCircle as ToastAlert, Info as ToastInfo } from "lucide-react";
import { heatAPI } from "./services/api";
import { getHomeRoute } from "./lib/roleRoutes";
// Fallback shown while a lazy route chunk loads — matches the boot spinner.
function PageFallback() {
  return (
    <div className="min-h-screen bg-[#0F0F1A] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-[#C5A059] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}
// <BrowserRouter> is mounted INSIDE App, so App renders ABOVE the router and
// never re-renders on a client-side navigation. Any branch App computes from
// window.location therefore freezes at the first document load. That is why
// closing the event workspace landed on a blank page: the URL changed to
// /events/automation, but the standalone <Routes> tree chosen at load time
// stayed mounted, matched no route, and rendered nothing. It fails the other
// way too - navigating INTO /event/:id from a dashboard page renders the
// sidebar with no workspace. Subscribing to the ROUTER's location here makes
// the branch re-evaluate on every navigation, which fixes the whole class
// rather than the one button that revealed it.
function PathSwitch({ children }) {
  useLocation();
  return children();
}

// Paths where the STAFFED Agent must NOT appear (public / client-facing / auth
// / print). Everywhere else in the admin app — Command Deck, the event
// workspace, every section — it shows. The event workspace (/event/:id) is
// intentionally NOT hidden so the agent stays reachable inside an open event.
const AGENT_HIDDEN_PATH = (p) =>
  p.startsWith('/live/') || p === '/live-experience' || p === '/card' ||
  p === '/check-availability' || p.startsWith('/event-command/') ||
  /^\/event\/[^/]+\/print$/.test(p) ||
  p.startsWith('/client-view/') || p.startsWith('/brief/') || p.startsWith('/songs/') ||
  p === '/embed/lead-form' || p.startsWith('/feedback/') || p === '/payment-success' ||
  p.startsWith('/call/') ||
  p.startsWith('/r/') ||
  p === '/terms' || p === '/privacy' ||
  p === '/login' || p === '/signup' || p === '/forgot-password' || p === '/reset-password' ||
  p.startsWith('/staff/register') || p.startsWith('/team/register') ||
  p === '/staff-login' || p === '/team-login';

// Public short review link: /r/{shortcode}. The SPA used to swallow this and
// bounce visitors to /login or the dashboard; now it renders standalone (no
// auth), resolves the real feedback target via the /api proxy, and replaces to
// it with the current query string (?via=…) preserved.
const SHORTLINK_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
function ShortLinkRedirect() {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const code = window.location.pathname.replace(/^\/r\//, '').split(/[/?#]/)[0];
    (async () => {
      try {
        const res = await fetch(`${SHORTLINK_BACKEND_URL}/api/short-links/${encodeURIComponent(code)}`);
        if (!res.ok) throw new Error('not found');
        const data = await res.json();
        const target = data && data.target;
        if (!target) throw new Error('no target');
        const qs = (window.location.search || '').replace(/^\?/, '');
        const joined = qs ? `${target}${target.includes('?') ? '&' : '?'}${qs}` : target;
        window.location.replace(joined);
      } catch (e) {
        setFailed(true);
      }
    })();
  }, []);
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0B0F1A', color: 'rgba(255,255,255,0.7)', fontFamily: 'Figtree, sans-serif', textAlign: 'center', padding: '24px' }} data-testid="short-link-redirect">
      <div>
        {failed ? (
          <>
            <p style={{ fontSize: '15px', margin: 0, color: '#fff' }}>This link has expired</p>
            <p style={{ fontSize: '13px', marginTop: '8px', color: 'rgba(255,255,255,0.4)' }}>Please ask for a fresh review link.</p>
          </>
        ) : (
          <p style={{ fontSize: '14px', margin: 0 }}>Opening your review…</p>
        )}
      </div>
    </div>
  );
}

// Single global launcher. Lives outside PathSwitch so it stays mounted across
// route changes (panel/conversation preserved) and renders over BOTH route
// trees, including the standalone event-workspace page. Admin only.
function GlobalAgentMount({ user }) {
  const { pathname } = useLocation();
  if (!user || user.role === 'staff') return null;
  if (AGENT_HIDDEN_PATH(pathname)) return null;
  // Entitlement gate — the STAFFED Agent only mounts when the tenant's plan
  // includes it (agent_qa). Grandfathered tenants always have it.
  if (!hasFlag(user, 'agent_qa')) return null;
  return (
    <>
      <AISupportAgent />
      <PendingCallNotifier />
      <ReviewCheckinNotifier />
    </>
  );
}

// Route-level entitlement guard. When a feature flag is OFF for the tenant,
// the route can't be reached by direct URL — it redirects to the home route.
// Grandfathered tenants (no entitlements) always pass (hasFlag → true).
function FlagRoute({ user, flag, redirectTo, children }) {
  if (hasFlag(user, flag)) return children;
  return <Navigate to={redirectTo} replace />;
}

function App() {
  // Detect when a newer build has been deployed under this open tab. Never
  // reloads silently — BuildUpdateBar offers the user a Refresh button.
  useBuildRefresh();
  const [user, setUser] = useState(null);
  const [userVersion, setUserVersion] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  
  // State for pending ratings modal
  const [pendingRatings, setPendingRatings] = useState([]);
  const [currentRatingIndex, setCurrentRatingIndex] = useState(0);
  const [showRatingModal, setShowRatingModal] = useState(false);
  useEffect(() => {
    // Restore user from appStorage/appSession for instant UI (no flash)
    const savedUser = appStorage.getItem('user') || appSession.getItem('user');
    const savedToken = appStorage.getItem('token') || appSession.getItem('token');
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
      } catch (e) {
        appStorage.removeItem('user');
        appSession.removeItem('user');
      }
    }
    // Verify session with the server. apiClient attaches the
    // `Authorization: Bearer` header from appStorage on every request
    // (Phase 2 cookie/`withCredentials` migration was rolled back — see
    // services/apiClient.js). We always call /auth/me on boot — even
    // when there's no appStorage state — so users with a freshly
    // re-issued token stay signed in across reloads.
    import('./services/apiClient').then(({ default: apiClient }) => {
      apiClient.get('/auth/me').then(res => {
        const freshUser = res.data;
        if (freshUser && freshUser.email) {
          setUser(freshUser);
          appStorage.setItem('user', JSON.stringify(freshUser));
          appSession.setItem('user', JSON.stringify(freshUser));
          // Role-based path correction is now handled declaratively by
          // <RouteGuard /> — see /app/frontend/src/components/RouteGuard.jsx.
        }
      }).catch((err) => {
        // Only nuke local state if the server actually rejected us (401).
        // Network errors should leave the cached user intact so an offline
        // refresh doesn't bounce the user to /login.
        if (err?.response?.status === 401) {
          appStorage.removeItem('token');
          appStorage.removeItem('user');
          appSession.removeItem('token');
          appSession.removeItem('user');
          setUser(null);
        }
      }).finally(() => {
        setIsLoading(false);
      });
    });
    // Suppress unused-var lint for the legacy savedToken read above —
    // it's intentionally kept for one boot cycle to migrate users
    // who still have a appStorage token but no cookie yet.
    void savedToken;
  }, []);
  // Listen for company settings updates to refresh header
  useEffect(() => {
    const handleCompanyUpdate = (event) => {
      const { companyName, companyLogo, logoBackground } = event.detail;
      
      // Update user state first, then force re-render
      setUser(prevUser => {
        if (prevUser) {
          const newUser = { 
            ...prevUser, 
            companyName, 
            companyLogo, 
            logoBackground 
          };
          appStorage.setItem('user', JSON.stringify(newUser));
          appSession.setItem('user', JSON.stringify(newUser));
          return newUser;
        }
        return prevUser;
      });
      
      // Force re-render by updating version after state update
      // Use setTimeout to ensure state update has propagated
      setTimeout(() => {
        setUserVersion(Date.now());
      }, 0);
    };
    window.addEventListener('companySettingsUpdated', handleCompanyUpdate);
    return () => window.removeEventListener('companySettingsUpdated', handleCompanyUpdate);
  }, []);
  // Live entitlement refresh — re-read the tenant's feature flags when the tab
  // regains focus so a plan change or override flip by a platform owner applies
  // without a full log-out/log-in. Only updates state when the map changed.
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const refresh = async () => {
      try {
        const { default: apiClient } = await import('./services/apiClient');
        const res = await apiClient.get('/entitlements/me');
        const next = res.data?.entitlements;
        if (cancelled || !next) return;
        setUser((prev) => {
          if (!prev) return prev;
          if (JSON.stringify(prev.entitlements) === JSON.stringify(next)) return prev;
          const merged = { ...prev, entitlements: next };
          appStorage.setItem('user', JSON.stringify(merged));
          appSession.setItem('user', JSON.stringify(merged));
          return merged;
        });
      } catch (e) { /* non-fatal — keep cached flags */ }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user ? (user.id || user.email) : null]);
  // Fetch pending ratings when the signed-in PERSON changes.
  //
  // ROOT CAUSE this fixes: the dependency was the `user` OBJECT. On every
  // boot this component sets `user` twice — once from appStorage for an
  // instant paint, then again from the /auth/me response — two different
  // object identities carrying the SAME person. The effect therefore ran
  // twice and asked `/heat-auto/pending-ratings` twice, ~170ms apart (the
  // /auth/me round trip). Measured on production opening one event
  // workspace, those two duplicate requests were the two slowest calls on
  // the page: 2193ms and 3219ms. One question, one call — depend on WHO is
  // signed in, not on which object is carrying them. No cache, no debounce.
  const signedInUserKey = user ? (user.id || user._id || user.email || 'signed-in') : null;
  useEffect(() => {
    if (signedInUserKey) {
      fetchPendingRatings();
      // Poll every 15 minutes for new pending ratings
      const interval = setInterval(fetchPendingRatings, 15 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [signedInUserKey]);
  const fetchPendingRatings = async () => {
    try {
      const data = await heatAPI.getPendingRatings();
      if (data.count > 0) {
        setPendingRatings(data.pendingRatings);
        setCurrentRatingIndex(0);
      } else {
        setPendingRatings([]);
      }
    } catch (error) {
      console.error('Failed to fetch pending ratings:', error);
    }
  };
  const triggerRatingModal = () => {
    if (pendingRatings.length > 0) {
      setCurrentRatingIndex(0);
      setShowRatingModal(true);
    }
  };
  const handleLogin = (userData, token) => {
    setUser(userData);
    if (userData) {
      // Store in both appStorage and appSession for PWA/iOS compat
      appStorage.setItem('user', JSON.stringify(userData));
      appSession.setItem('user', JSON.stringify(userData));
      // Store token for Bearer auth
      if (token) {
        appStorage.setItem('token', token);
        appSession.setItem('token', token);
      }
      const count = parseInt(appStorage.getItem('staffed_login_count') || '0', 10);
      appStorage.setItem('staffed_login_count', String(count + 1));
    }
  };
  const handleLogout = async () => {
    // Call backend logout endpoint
    try {
      const { default: apiClient } = await import('./services/apiClient');
      await apiClient.post('/auth/logout');
    } catch (e) { /* ignore */ }
    // Clear ALL auth data from both storage types
    appStorage.removeItem('token');
    appStorage.removeItem('user');
    appSession.removeItem('token');
    appSession.removeItem('user');
    setUser(null);
    setPendingRatings([]);
    setTimeout(() => {
      window.location.href = '/login?logout=true';
    }, 100);
  };
  const handleRatingComplete = () => {
    // Move to next pending rating
    if (currentRatingIndex < pendingRatings.length - 1) {
      setCurrentRatingIndex(prev => prev + 1);
    } else {
      // All ratings done, close modal and refresh
      setShowRatingModal(false);
      setCurrentRatingIndex(0);
      // Refresh pending ratings list
      setTimeout(() => fetchPendingRatings(), 500);
    }
  };
  const handleSkipRating = () => {
    // Move to next or close
    if (currentRatingIndex < pendingRatings.length - 1) {
      setCurrentRatingIndex(prev => prev + 1);
    } else {
      setShowRatingModal(false);
      setCurrentRatingIndex(0);
      // Refresh pending ratings list
      setTimeout(() => fetchPendingRatings(), 500);
    }
  };
  const currentRating = pendingRatings[currentRatingIndex];
  // Determine home route based on user role (delegated to shared util)
  const homeRoute = getHomeRoute(user);
  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0F0F1A] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#C5A059] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return (
    <FeaturesProvider>
      <AutoSaveProvider>
      <BudgetSyncProvider>
      <EventProvider>
      <NotificationProvider>
      <TooltipProvider delayDuration={300}>
      <PullToRefresh>
      <BrowserRouter>
        <div className="App min-h-screen" key={user ? 'logged-in' : 'logged-out'}>
          {/* Check if on public standalone route */}
          <PathSwitch>{() => (
            window.location.pathname.startsWith('/live/') || window.location.pathname === '/live-experience' || window.location.pathname === '/card' || window.location.pathname === '/check-availability' || window.location.pathname.startsWith('/event-command/') || /^\/event\/[^/]+$/.test(window.location.pathname) || /^\/event\/[^/]+\/print$/.test(window.location.pathname) || window.location.pathname.startsWith('/client-view/') || window.location.pathname.startsWith('/brief/') || window.location.pathname.startsWith('/songs/') || window.location.pathname === '/embed/lead-form' || window.location.pathname.startsWith('/feedback/') || window.location.pathname === '/payment-success' || window.location.pathname.startsWith('/call/') || window.location.pathname.startsWith('/r/') || window.location.pathname === '/terms' || window.location.pathname === '/privacy' ? (
            <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/live/all/:token" element={<LiveAllEvents />} />
              <Route path="/live/:token" element={<LiveControlCenter />} />
              <Route path="/brief/:token" element={<BriefView />} />
              <Route path="/live-experience" element={<LiveExperiencePage />} />
              {/* Public entry points — always registered. /card is the Business
                  Card QR destination; /check-availability is a public booking form.
                  Their ADMIN nav links stay hidden; only the public routes render. */}
              <Route path="/card" element={<DigitalCardPage />} />
              <Route path="/check-availability" element={<CheckAvailability />} />
              <Route path="/event-command/:eventId" element={<Navigate to={window.location.pathname.replace('/event-command/', '/event/')} replace />} />
              <Route path="/event/:eventId" element={<ErrorBoundary><EventWorkspacePage /></ErrorBoundary>} />
              <Route path="/event/:eventId/print" element={<EventPrintPage />} />
              <Route path="/client-view/:token" element={<ClientViewPage />} />
              <Route path="/songs/:token" element={<SongRequestPage />} />
              <Route path="/embed/lead-form" element={<EmbedLeadForm />} />
              <Route path="/feedback/:token" element={<EventFeedbackPage />} />
              <Route path="/r/:shortcode" element={<ShortLinkRedirect />} />
              <Route path="/call/confirm/:token" element={<CallConfirmPage />} />
              <Route path="/call/reschedule/:token" element={<CallReschedulePage />} />
              <Route path="/payment-success" element={<PaymentSuccessPage />} />
              <Route path="/terms" element={<LegalPage doc="terms" />} />
              <Route path="/privacy" element={<LegalPage doc="privacy" />} />
              {/* Catch-all — an unmatched public path must never render a fully
                  blank page. Send it to the main app entry instead. */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
          ) : user ? (
            <RolePreviewProvider user={user}>
              <AuthenticatedShell
                realUser={user}
                homeRoute={homeRoute}
                handleLogout={handleLogout}
                pendingRatings={pendingRatings}
                showRatingModal={showRatingModal}
                currentRating={currentRating}
                setShowRatingModal={setShowRatingModal}
                handleRatingComplete={handleRatingComplete}
                handleSkipRating={handleSkipRating}
                triggerRatingModal={triggerRatingModal}
              />
            </RolePreviewProvider>
          ) : (
              <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/login" element={<Login onLogin={handleLogin} />} />
                <Route path="/signup" element={<Signup onLogin={handleLogin} />} />
                <Route path="/" element={<Navigate to="/signup" replace />} />
                <Route path="/staff-login" element={<Navigate to="/login" replace />} />
                <Route path="/team-login" element={<Navigate to="/login" replace />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/staff/register" element={<StaffRegister onLogin={handleLogin} />} />
                <Route path="/team/register" element={<TeamRegister onLogin={handleLogin} />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
              </Suspense>
            ))}</PathSwitch>
            {/* BLOCK S7b 1 — the How-To card host is mounted ONCE here, above
                PathSwitch, so it renders for EVERY signed-in route: both the
                standalone tree (which owns /event/:id) and the AuthenticatedShell
                tree. A single mount, no per-event copy; it survives switching
                between the two trees because it lives outside PathSwitch. */}
            {user && <HowToCard identity={user.id || user._id || user.email || null} />}
            {user && <GlobalAgentMount user={user} />}
            {user && <PackageRequestsDock />}
            <Toaster />
            {/* Toasts are portaled to <body> with the max z-index so they are
                the TOP-MOST layer above everything — the S agent badge (and
                its breathing ring), headers, panels and modals — and can never
                be trapped inside a transformed ancestor (e.g. PullToRefresh)
                or covered. Look/position/timing unchanged; z-order only. */}
            {createPortal(
              <SonnerToaster
                position="top-right"
                theme="dark"
                offset={{ top: 76, right: 20 }}
                mobileOffset={{ top: 16, right: 16, left: 16 }}
                gap={12}
                visibleToasts={3}
                duration={6000}
                closeButton
                style={{ zIndex: 2147483647 }}
                icons={{
                  success: <ToastCheck size={18} strokeWidth={2.4} />,
                  error: <ToastAlert size={18} strokeWidth={2.4} />,
                  info: <ToastInfo size={18} strokeWidth={2.4} />,
                  warning: <ToastInfo size={18} strokeWidth={2.4} />,
                }}
              />,
              document.body
            )}
            <EnvironmentBadge />
            <BuildUpdateBar />
          </div>
        </BrowserRouter>
        </PullToRefresh>
        </TooltipProvider>
        </NotificationProvider>
        </EventProvider>
        </BudgetSyncProvider>
        </AutoSaveProvider>
    </FeaturesProvider>
  );
}
/**
 * AuthenticatedShell — extracted to allow consumption of RolePreviewContext
 * via hooks. Receives the real user + handlers; reads the effectiveUser
 * (with role override) from context and threads it through DashboardLayout
 * + RouteGuard so chrome and routing always agree on which role to render.
 */
function AuthenticatedShell({ realUser, homeRoute, handleLogout, pendingRatings, showRatingModal, currentRating, setShowRatingModal, handleRatingComplete, handleSkipRating, triggerRatingModal }) {
  const { effectiveUser, isPreviewing } = useRolePreview();
  const userForChrome = effectiveUser || realUser;
  // When previewing, the effective home route follows the previewed role so
  // the Sidebar + getHomeRoute redirects match what the previewed user sees.
  const effectiveHome = isPreviewing ? getHomeRoute(effectiveUser) : homeRoute;
  return (
    <>
      <RolePreviewBanner />
      <DashboardLayout
        user={userForChrome}
        realUser={realUser}
        onLogout={handleLogout}
        pendingRatingsCount={pendingRatings.length}
        onShowRatings={triggerRatingModal}
      >
        {/* First-Time User Onboarding Tour */}
        <OnboardingTour />
        {/* Global command palette — Cmd/Ctrl+K to jump anywhere */}
        <CommandPalette />
        <RouteGuard user={userForChrome}>
        <Suspense fallback={<PageFallback />}>
        <Routes>
          {/* Default route - role-aware redirect */}
          <Route path="/" element={<Navigate to={effectiveHome} replace />} />
          <Route path="/login" element={<Navigate to={effectiveHome} replace />} />
          <Route path="/dashboard" element={<Navigate to={effectiveHome} replace />} />
          <Route path="/events" element={<EventList />} />
          <Route path="/events/automation" element={<Navigate to="/events/pipeline" replace />} />
          <Route path="/templates" element={<Navigate to="/settings/setup/proposal-terms" replace />} />
          <Route path="/events/pipeline" element={<PipelinePage />} />
          <Route path="/events/locations" element={<Locations />} />
          <Route path="/events/shift-reminders" element={<ReminderHealthPage />} />
          <Route path="/events/budgets" element={<FlagRoute user={realUser} flag="budgets" redirectTo={effectiveHome}><EventBudgets /></FlagRoute>} />
          <Route path="/payouts" element={<FlagRoute user={realUser} flag="payouts" redirectTo={effectiveHome}><PayoutsPage /></FlagRoute>} />
          <Route path="/events/clients" element={<Clients />} />
          <Route path="/reviews" element={<ReviewsActivityPage />} />
          <Route path="/live-leads" element={<FlagRoute user={realUser} flag="live_leads" redirectTo={effectiveHome}><LiveLeadsPage /></FlagRoute>} />
          <Route path="/qr-codes" element={<FlagRoute user={realUser} flag="qr_codes" redirectTo={effectiveHome}><QrCodesPage /></FlagRoute>} />
          {/*
            Tier 2B (Feb 2026) — Legacy standalone Control Center route.
            Redirects to the canonical `/event/:eventId?section=control`
            workspace tab and logs a tracking hit. The EventControlCenter
            component itself is unchanged and still rendered (embedded)
            inside EventWorkspacePage. Will be removed entirely in Tier 2D
            once tracking confirms zero residual traffic.
          */}
          <Route path="/events/:eventId/control-center" element={<LegacyControlCenterRedirect />} />
          <Route path="/event/:eventId" element={<EventWorkspacePage />} />
          <Route path="/event/:eventId/print" element={<EventPrintPage />} />
          <Route path="/staff" element={<StaffList />} />
          <Route path="/staff/availability" element={<StaffAvailability />} />
          <Route path="/staff/roles" element={<StaffRoles />} />
          <Route path="/staff/announcement" element={<TeamAnnouncement />} />
          <Route path="/staff/announcements" element={<StaffAnnouncementsPage />} />
          <Route path="/staff/leaderboard" element={<HeatLeaderboard />} />
          <Route path="/staff/checkin" element={<GPSCheckin />} />
          <Route path="/staff/my-profile" element={<StaffMyProfile />} />
          <Route path="/staff/my-assignments" element={<MyAssignmentsPage />} />
          {/* Deep-linkable brief (Block 24). Same page, drawer pre-opened for
              :eventId. The drawer fetches the brief by id on its own, so this
              resolves even for an event missing from the assignments list. */}
          <Route path="/staff/my-assignments/:eventId" element={<MyAssignmentsPage />} />
          <Route path="/staff/my-availability" element={<MyAvailability />} />
          <Route path="/staff/help" element={<StaffHelpCenter />} />
          <Route path="/staff-portal" element={<StaffPortal />} />
          {/* Block 25 (2026-08-18) — the standalone staff brief is retired.
              Both paths are PERMANENT redirects: assignment emails and web-push
              payloads already in the wild point at /my-events/{id}. The drawer
              resolves either id form on its own, so legacy links still land on
              the right event. */}
          <Route path="/my-events" element={<LegacyBriefRedirect />} />
          <Route path="/my-events/:eventId" element={<LegacyBriefRedirect />} />
          <Route path="/planner" element={<Navigate to="/planner/vendors" replace />} />
          <Route path="/planner/vendors" element={<VendorDirectory />} />
          <Route path="/planner/venues" element={<VenueDirectory />} />
          <Route path="/planner/favorites" element={<Navigate to="/planner/vendors" replace />} />
          <Route path="/account" element={<Navigate to="/account/company" replace />} />
          <Route path="/account/company" element={<Navigate to="/settings/setup/business-profile" replace />} />
          <Route path="/account/team" element={<Team />} />
          <Route path="/account/billing" element={<Navigate to="/settings/setup/billing" replace />} />
          <Route path="/account/features" element={<Features />} />
          <Route path="/account/preferred-vendors" element={<PreferredVendors />} />
          <Route path="/control-center" element={<MyControlCenter />} />
          {/* Team Chat retired from the UI (hide-only — backend event-chat data,
              endpoints, and all Event Control wiring stay intact and reversible).
              Old links/bookmarks quietly redirect to the role's home page. */}
          <Route path="/my-chats" element={<Navigate to={effectiveHome} replace />} />
          <Route path="/my-chats/:eventId" element={<Navigate to={effectiveHome} replace />} />
          <Route path="/admin/verifications" element={<AdminVerifications />} />
          <Route path="/admin/unattributed-leads" element={isPlatformOwner(realUser) ? <UnattributedLeadsPage /> : <Navigate to="/admin" replace />} />
          <Route path="/admin/entitlements" element={isPlatformOwner(realUser) ? <EntitlementsAdminPage /> : <Navigate to="/admin" replace />} />
          <Route path="/admin/payment-sweep-repair" element={isPlatformOwner(realUser) ? <PaymentSweepRepairPage /> : <Navigate to="/admin" replace />} />
          <Route path="/admin/payment-reminder-scans" element={isPlatformOwner(realUser) ? <PaymentReminderScansPage /> : <Navigate to="/admin" replace />} />
          <Route path="/settings/payment-fees" element={<PaymentFeeSettingsPage />} />
          <Route path="/settings/setup" element={<SetupPage />} />
          <Route path="/settings/setup/:section" element={<SetupPage />} />
          <Route path="/settings/event-types" element={<EventTypesPage />} />
          <Route path="/settings/embed-code" element={<EmbedCodePage />} />
          <Route path="/settings/package-contents" element={<PackageContentsEditor />} />
          <Route path="/settings/guest-ranges" element={<Navigate to="/settings/event-types#guest-ranges" replace />} />
          <Route path="/admin" element={<AdminPanel />} />
          {/* Tier 5C Pass 8 — internal motion-system showcase (platform owners only) */}
          <Route path="/internal/motion-demo" element={isPlatformOwner(realUser) ? <MotionDemo /> : <Navigate to={effectiveHome} replace />} />
          {/* Catch-all */}
          <Route path="*" element={<Navigate to={effectiveHome} replace />} />
        </Routes>
        </Suspense>
        </RouteGuard>
      </DashboardLayout>
      {/* STAFFED Agent is mounted ONCE globally (see GlobalAgentMount, outside
          PathSwitch) so it persists across every admin screen — including the
          standalone event-workspace route — without a second launcher here. */}
      {/* Quick Rating Modal - Shows on login if there are pending ratings */}
      {showRatingModal && currentRating && (
        <QuickRatingModal
          isOpen={showRatingModal}
          onClose={() => setShowRatingModal(false)}
          staff={currentRating}
          eventId={currentRating.eventId}
          eventName={currentRating.eventName}
          onRated={handleRatingComplete}
          onSkip={handleSkipRating}
        />
      )}
      {/* Beta Feedback Button - Show on all pages when logged in */}
      <BetaFeedbackButton />
    </>
  );
}
export default App;
